"use client";

import { useState, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, ImageIcon, Loader2 } from "lucide-react";
import { numberToMoney } from "@/util/moneyConversion";
import { ProductsService } from "@/services/ProductsService";
import type { ProductsOutput } from "@/interfaces";
import { X } from "lucide-react";
import { toast } from "sonner";

interface MultiProductSelectorProps {
  value: string[] | undefined;
  onChange: (selectedIds: string[]) => void;
  className?: string;
  placeholder?: string;
  selectionMode?: "single" | "multiple";
}

export default function MultiProductSelector({
  value = [],
  onChange,
  className = "",
  placeholder = "Selecione um produto",
  selectionMode = "multiple",
}: MultiProductSelectorProps) {
  const [productsMap, setProductsMap] = useState<
    Record<string, ProductsOutput>
  >({});
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [searchResults, setSearchResults] = useState<ProductsOutput[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const inputContainerRef = useRef<HTMLDivElement | null>(null);
  const productsService = new ProductsService();

  useEffect(() => {
    const fetchProducts = async () => {
      const idsToFetch = value.filter((id) => !productsMap[id]);
      if (idsToFetch.length === 0) return;

      try {
        const fetchPromises = idsToFetch.map((id) =>
          productsService.findById(id, { expand: ["file"] })
        );
        const fetched = await Promise.all(fetchPromises);
        setProductsMap((prev) => {
          const updated = { ...prev };
          fetched.forEach((p) => {
            updated[p.id] = p;
          });
          return updated;
        });
      } catch {
        toast.error("Erro ao buscar produtos");
      }
    };
    fetchProducts();
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputContainerRef.current &&
        !inputContainerRef.current.contains(event.target as Node)
      ) {
        setSearchResults([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleAddProduct = async (product: ProductsOutput) => {
    if (!product.id) return;
    setProductsMap((prev) => ({ ...prev, [product.id]: product }));
    if (selectionMode === "single") {
      onChange([product.id]);
      setSearchQuery(product.name);
    } else {
      if (value.includes(product.id)) return;
      onChange([...value, product.id]);
      setSearchQuery("");
    }
    setSearchResults([]);
  };

  const searchProducts = async (query: string) => {
    setIsSearching(true);
    try {
      const { data } = await productsService.findAll({
        query,
        limit: 20,
        expand: "file",
      });
      setSearchResults(data);
    } catch {
      toast.error("Erro ao buscar produtos");
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = (query: string) => {
    if (selectionMode === "single" && value.length > 0) {
      onChange([]);
    }
    setSearchQuery(query);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      searchProducts(query);
    }, 300);
  };

  const handleFocus = () => {
    if (!searchQuery.trim()) {
      searchProducts("");
    }
  };

  const handleRemove = (id: string) => {
    onChange(value.filter((v) => v !== id));
    if (selectionMode === "single") {
      setSearchQuery("");
    }
  };

  return (
    <div className={`space-y-3 ${className}`} ref={inputContainerRef}>
      <div className="relative max-w-xs">
        <Input
          value={
            selectionMode === "single" && value.length > 0
              ? productsMap[value[0]]?.name || searchQuery
              : searchQuery
          }
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={handleFocus}
          placeholder={placeholder}
          className="pl-8"
        />
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500">
          {isSearching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Search className="h-4 w-4" />
          )}
        </span>
        {selectionMode === "single" && value.length > 0 && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500"
            onClick={() => handleRemove(value[0])}
          >
            <X className="h-4 w-4" />
          </button>
        )}
        {searchResults.length > 0 && (
          <div className="absolute z-20 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
            {searchResults.map((product) => (
              <div
                key={product.id}
                className="p-2 hover:bg-gray-50 cursor-pointer flex items-center gap-3 border-b last:border-b-0"
                onClick={() => handleAddProduct(product)}
              >
                <div className="h-10 w-10 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                  {product.file?.url ? (
                    <img
                      src={product.file.url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="h-5 w-5 text-gray-400" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{product.name}</p>
                  <p className="text-sm text-[#EE7B11] font-semibold">
                    {numberToMoney(product.unitPrice)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {selectionMode === "multiple" && value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((id) => {
            const product = productsMap[id];
            return (
              <Badge
                key={id}
                variant="secondary"
                className="rounded-md px-3 py-1 flex items-center gap-1"
              >
                {product ? product.name : id}
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRemove(id);
                  }}
                  className="h-4 w-4 focus:outline-none"
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
}
